import { api } from './api.util';
import { API_BASE_URL } from '../config/api.config';

// Base API URL for free downloads
const BASE_URL = '/free-downloads';

/**
 * Get all free downloads
 * @param {Object} params - Query parameters
 * @param {string} params.category - Filter by category
 * @param {boolean} params.active - Filter by active status
 * @returns {Promise<Object>} API response with free downloads data
 */
export const getAllFreeDownloads = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.category) {
      queryParams.append('category', params.category);
    }
    
    if (params.active !== undefined) {
      queryParams.append('active', params.active.toString());
    }
    
    const url = queryParams.toString() ? `${BASE_URL}?${queryParams}` : BASE_URL;
    
    const response = await api.get(url);
    
    return response;
  } catch (error) {
    console.error('Error fetching free downloads:', error);
    throw error;
  }
};

/**
 * Get free download by ID
 * @param {string} id - Free download ID
 * @returns {Promise<Object>} API response with free download data
 */
export const getFreeDownloadById = async (id) => {
  try {
    const response = await api.get(`${BASE_URL}/${id}`);
    
    return response;
  } catch (error) {
    console.error(`Error fetching free download ${id}:`, error);
    throw error;
  }
};

/**
 * Get free download categories
 * @returns {Promise<Object>} API response with categories data
 */
export const getFreeDownloadCategories = async () => {
  try {
    const response = await api.get(`${BASE_URL}/categories`);
    
    return response;
  } catch (error) {
    console.error('Error fetching free download categories:', error);
    throw error;
  }
};

/**
 * Download free download file
 * @param {string} id - Free download ID
 * @returns {string} Download URL
 */
export const downloadFreeDownload = (id) => {
  // Return the download URL for direct navigation
  return `${API_BASE_URL}/api${BASE_URL}/${id}/download`;
};

/**
 * Create a new free download (admin only)
 * @param {Object} data - Free download data
 * @returns {Promise<Object>} API response
 */
export const createFreeDownload = async (data) => {
  try {
    const response = await api.post(BASE_URL, data);
    
    return response;
  } catch (error) {
    console.error('Error creating free download:', error);
    throw error;
  }
};

/**
 * Upload free download file (admin only)
 * @param {FormData} formData - Form data with file and metadata
 * @returns {Promise<Object>} API response
 */
export const uploadFreeDownload = async (formData) => {
  try {
    const response = await api.upload(`${BASE_URL}/upload`, formData);
    
    return response;
  } catch (error) {
    console.error('Error uploading free download:', error);
    throw error;
  }
};

/**
 * Update free download by ID (admin only)
 * @param {string} id - Free download ID
 * @param {Object} data - Updated data
 * @returns {Promise<Object>} API response
 */
export const updateFreeDownload = async (id, data) => {
  try {
    const response = await api.put(`${BASE_URL}/${id}`, data);
    
    return response;
  } catch (error) {
    console.error(`Error updating free download ${id}:`, error);
    throw error;
  }
};

/**
 * Delete free download by ID (admin only)
 * @param {string} id - Free download ID
 * @returns {Promise<Object>} API response
 */
export const deleteFreeDownload = async (id) => {
  try {
    const response = await api.delete(`${BASE_URL}/${id}`);
    
    return response;
  } catch (error) {
    console.error(`Error deleting free download ${id}:`, error);
    throw error;
  }
};
